import { useFlags } from "launchdarkly-react-client-sdk";
import { createContext, useContext, useMemo } from "react";

import { useAppSelector } from "app/hooks";
import { PER_REPORT_ACCESS } from "constants/feature-flags";
import {
  selectCurrentUser,
  selectUserClinicsIds
} from "features/auth/auth-slice";
import useUserRoles from "hooks/use-user-roles.hook";

import {
  ClinicScope,
  FINANCIAL_ONLY_REPORT_IDS,
  PersonClinics,
  REPORT_IDS,
  ReportId
} from "../report-registry.config";

export type ReportAccess = {
  canView: boolean;
  canDownload: boolean;
  // Frontend-only descriptors — the backend never scopes a report queryset on these.
  clinicScope: ClinicScope;
  selfOnly: boolean;
  /** Clinics the user may filter by when `clinicScope` is "selected". */
  selectedClinicIds: number[];
};

export type ReportAccessMap = Record<ReportId, ReportAccess>;

const FULL_ACCESS: ReportAccess = {
  canView: true,
  canDownload: true,
  clinicScope: "all",
  selfOnly: false,
  selectedClinicIds: []
};

const NO_ACCESS: ReportAccess = {
  canView: false,
  canDownload: false,
  clinicScope: "all",
  selfOnly: false,
  selectedClinicIds: []
};

const NO_IDS: number[] = [];

type ResolvedClinics = Pick<ReportAccess, "clinicScope" | "selectedClinicIds">;

const ALL_CLINICS: ResolvedClinics = {
  clinicScope: "all",
  selectedClinicIds: []
};

/**
 * A clinic list resolved for a report. An empty list scopes to nothing while the request goes out
 * unscoped, so it means every clinic, as `useReportFilterLock` already treats it.
 */
const onlyClinics = (ids: number[]): ResolvedClinics =>
  ids.length
    ? { clinicScope: "selected", selectedClinicIds: ids }
    : ALL_CLINICS;

/**
 * The person's clinic rule, which every `default` entry and every unconfigured report follows.
 * `assigned` is read from the user's clinics as they are now, so a change on the Clinics tab reaches
 * their reports on the next app load with nothing re-saved. No rule (a blob saved before it
 * existed) is every clinic.
 */
export const resolvePersonClinics = (
  clinics: PersonClinics | undefined,
  assignedClinicIds: number[]
): ResolvedClinics => {
  switch (clinics?.scope) {
    case "assigned":
      return onlyClinics(assignedClinicIds);
    case "selected":
      return onlyClinics(clinics.selected_clinic_ids ?? []);
    default:
      return ALL_CLINICS;
  }
};

/**
 * The unconfigured map, built once per download right rather than per hook instance.
 *
 * This is the branch EVERY user takes with the flag off, in simple mode, or with an empty blob — and
 * `useReportFilterLock` reaches this hook from `CustomFiltersList` and `useCustomFilters`, which are
 * mounted app-wide (billing, patients, inventory, settings...). Building a fresh 57-entry map per
 * filter bar meant ~6 KB of transient allocation on every non-reports screen, for users this feature
 * is meant to leave untouched. Only two shapes exist, so they are cached.
 *
 * Safe to share: consumers only read (`permissions[reportId]`), and every key already pointed at one
 * shared `ReportAccess` object.
 */
const unconfiguredMaps = new Map<boolean, ReportAccessMap>();

const unconfiguredMapFor = (canDownload: boolean): ReportAccessMap => {
  const cached = unconfiguredMaps.get(canDownload);
  if (cached) {
    return cached;
  }

  const access: ReportAccess = { ...FULL_ACCESS, canDownload };
  const map = {} as ReportAccessMap;
  REPORT_IDS.forEach((reportId) => {
    map[reportId] = access;
  });

  unconfiguredMaps.set(canDownload, map);
  return map;
};

/**
 * One shared resolution for the whole reports module. `ReportPermissionsProvider` (mounted around
 * the module in reports.routes.tsx) computes the map once; every `useReportPermissions` call below
 * it — the nav, the route guard, ~75 filter bars and their pickers — reads the same object instead
 * of each deriving its own. Consumers outside the provider (the admin matrix) compute locally.
 */
export const ReportPermissionsContext = createContext<ReportAccessMap | null>(
  null
);

/**
 * Resolve the current user's access to every report from their `report_permissions` blob.
 *
 * Shaped after the dashboard's `can_see_manufacturer_cost` gate: a per-user value read straight off
 * `currentUser`, used to gate UI only. Nothing here changes an API call or a query.
 *
 * Resolution order:
 *  - flag off, or the user was never configured (no `advanced` mode) → full access, so nobody loses
 *    reports until an admin deliberately configures them;
 *  - a switched-in admin keeps full access (home-practice restrictions are meaningless there);
 *  - otherwise a report's explicit entry is **authoritative and overrides group membership** — an
 *    Administrator/Suno user with `view: false` loses that report. Previously the admin group
 *    short-circuited the whole resolution, so anything configured for an admin was silently ignored;
 *  - a report with no entry (e.g. one added to the catalogue after this user was configured) falls
 *    back to the group baseline, so a new report never disappears for the people who administer the
 *    practice.
 */
export default function useReportPermissions(): ReportAccessMap {
  // The provider computes with a null context (it sits above its own Provider), everyone under it
  // shares that result. Hooks must still run unconditionally; the memo below skips the map build.
  const shared = useContext(ReportPermissionsContext);
  const flags = useFlags();
  const perReportAccessEnabled = Boolean(flags[PER_REPORT_ACCESS]);
  const currentUser = useAppSelector(selectCurrentUser);
  const assignedClinicIds = useAppSelector(selectUserClinicsIds) ?? NO_IDS;
  const {
    isAdminOrBusinessOwner,
    isSwitchedSession,
    canDownloadReports,
    isReportsOnlyUser
  } = useUserRoles();

  return useMemo(() => {
    if (shared) return shared;
    const permissions = currentUser?.report_permissions;
    const advanced =
      perReportAccessEnabled &&
      permissions?.mode === "advanced" &&
      !isSwitchedSession;

    // `canDownloadReports` applies to everyone with no admin bypass, so the baseline has to honour
    // it too — otherwise switching an admin to advanced mode silently hands back a download right
    // that was explicitly turned off for them.
    const personClinics = resolvePersonClinics(
      permissions?.clinics,
      assignedClinicIds
    );
    const baseline = isAdminOrBusinessOwner
      ? { ...FULL_ACCESS, canDownload: canDownloadReports, ...personClinics }
      : NO_ACCESS;

    const resolveAccess = (reportId: ReportId): ReportAccess => {
      if (!advanced) {
        return { ...FULL_ACCESS, canDownload: canDownloadReports };
      }
      const entry = permissions?.reports?.[reportId];
      if (!entry) return baseline;
      return {
        canView: Boolean(entry.view),
        canDownload: Boolean(entry.view && entry.download),
        selfOnly: Boolean(entry.self_only),
        ...(entry.clinic_scope === "default"
          ? personClinics
          : {
              clinicScope: entry.clinic_scope ?? "all",
              selectedClinicIds: entry.selected_clinic_ids ?? []
            })
      };
    };

    // Financial Reports Only Access denies every non-financial report outright, regardless of
    // mode or the PER_REPORT_ACCESS flag; financial reports keep whatever the ordinary
    // resolution grants them (so advanced-mode grants still intersect).
    if (isReportsOnlyUser) {
      const map = {} as ReportAccessMap;
      REPORT_IDS.forEach((reportId) => {
        map[reportId] = FINANCIAL_ONLY_REPORT_IDS.has(reportId)
          ? resolveAccess(reportId)
          : NO_ACCESS;
      });
      return map;
    }

    if (!advanced) {
      return unconfiguredMapFor(canDownloadReports);
    }

    const map = {} as ReportAccessMap;
    REPORT_IDS.forEach((reportId) => {
      map[reportId] = resolveAccess(reportId);
    });

    return map;
  }, [
    shared,
    perReportAccessEnabled,
    currentUser?.report_permissions,
    isAdminOrBusinessOwner,
    isSwitchedSession,
    canDownloadReports,
    isReportsOnlyUser,
    assignedClinicIds
  ]);
}
